import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSequence } from './reveal'

/** Milliseconds the box takes to settle on its new height. */
const SETTLE = 300

/**
 * Changes what it shows without a jump.
 *
 * The first contents take their turn in the page's opening sequence. After
 * that the old contents fade out, the new fade in after them, and the box eases
 * from the one height to the other, so whatever sits below is carried rather
 * than thrown. Nothing is moved or scaled: an ancestor's transform would take
 * a card's open, fixed box with it.
 *
 * The height is only held while a change is under way. At rest the box is as
 * tall as what it holds, so a picture or a font arriving late does not set
 * everything below it sliding.
 */
export function Swap({
  id,
  order = 0,
  children,
}: {
  id: string
  /** Its place in the page's opening sequence, for the first contents only. */
  order?: number
  children: ReactNode
}) {
  const inner = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number>()
  const [isChanging, setIsChanging] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)
  const shown = useRef(id)
  const isStill = useReducedMotion()
  const due = useSequence()
  const isInView = useInView(inner, { once: true })

  /* Only the first contents wait their turn, and from when they are in view. */
  const turn = useMemo(() => (isInView ? due(order) : 0), [isInView])

  useEffect(() => {
    const node = inner.current

    if (!node) {
      return
    }

    const observer = new ResizeObserver(() => setHeight(node.offsetHeight))

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (shown.current === id) {
      return
    }

    shown.current = id
    setIsChanging(true)

    /* Long enough for the old to leave, the new to arrive and the box to settle. */
    const timer = setTimeout(() => setIsChanging(false), SETTLE + 250)

    return () => clearTimeout(timer)
  }, [id])

  return (
    <div
      style={
        isChanging && !isStill ? { height, transition: `height ${SETTLE}ms ease-out` } : undefined
      }
    >
      <div ref={inner}>
        <AnimatePresence mode="wait">
          <motion.div
            key={id}
            initial={{ opacity: 0 }}
            animate={
              isInView
                ? {
                    opacity: 1,
                    transition: { duration: 0.22, delay: hasOpened ? 0 : turn, ease: 'linear' },
                  }
                : undefined
            }
            exit={{
              opacity: 0,
              pointerEvents: 'none',
              transition: { duration: 0.1, ease: 'linear' },
            }}
            onAnimationComplete={() => setHasOpened(true)}
            /*
             * Having this keeps the fade on the main thread. Handed to the
             * compositor, a finished fade lets go a frame before the old
             * contents are removed, and they show at full strength for it.
             */
            onUpdate={() => {}}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}
